import type { Key, ReactNode } from 'react';
import type { TColumn } from '@/ui/Table/types';
import { Flex, Typography } from '@ui';
import clsx from 'clsx';
import { isDataColumn } from '@/ui/Table/types';
import styles from './Table.module.css';

type Props<T extends object> = {
    variant?: 'primary' | 'secondary';
    data: T[];
    columns: TColumn<T>[];
    rowKey: (entity: T) => Key;
    block?: boolean;
    empty?: {
        icon: ReactNode;
        title: string;
    };
};

export const Table = <T extends object>({ variant = 'primary', data, columns, rowKey, block, empty }: Props<T>) => (
    <>
        <table className={clsx(styles.table, styles[`variant-${variant}`], block && styles.block)}>
            <thead>
                <tr>
                    {columns.map(column => (
                        <th key={`th${String(isDataColumn(column) ? column.dataProperty : column.key)}`}>
                            {column.title}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {data.map(row => (
                    <tr key={`tr${rowKey(row)}`}>
                        {columns.map((column) => {
                            const computedValue = isDataColumn(column)
                                ? column.render?.(row[column.dataProperty], row) || String(row[column.dataProperty])
                                : column.render(undefined, row);

                            return (
                                <td key={`td${String(isDataColumn(column) ? column.dataProperty : column.key)}`}>
                                    {computedValue}
                                </td>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </table>
        {!data.length && empty && (
            <Flex flex="1 0" align="center" vertical gap={8} className={styles.emptyContainer}>
                {empty.icon}
                <Typography.Paragraph level={3}>{empty.title}</Typography.Paragraph>
            </Flex>
        )}
    </>
);
